import { useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { MoreHorizontal, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { useTeams } from '@/features/rocks/hooks/useTeams';
import { useAgenda, useAddSection, useReorderSections, useUpdateAgenda } from '../hooks';
import { AgendaSectionRow } from './AgendaSectionRow';
import type { AgendaSection, ToolType } from '../types';

// =============================================================================
// Tool Section Definitions
// =============================================================================

const TOOL_SECTIONS: { name: string; toolType: ToolType; duration: number }[] = [
  { name: 'SEGUE', toolType: 'segue', duration: 300 },
  { name: 'SCORECARD', toolType: 'scorecard', duration: 300 },
  { name: 'ROCK REVIEW', toolType: 'rock_review', duration: 300 },
  { name: 'CUSTOMER / EMPLOYEE HEADLINES', toolType: 'headlines', duration: 300 },
  { name: 'TO-DO LIST', toolType: 'todo_list', duration: 300 },
  { name: 'IDS™', toolType: 'ids', duration: 3600 },
  { name: 'CONCLUDE', toolType: 'conclude', duration: 300 },
];

// =============================================================================
// Duration formatting helper
// =============================================================================

function formatTotalDuration(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
  if (minutes > 0 || hours === 0) parts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`);
  return parts.join(' ');
}

// =============================================================================
// AgendaEditor Component
// =============================================================================

interface AgendaEditorProps {
  agendaId: string;
}

export function AgendaEditor({ agendaId }: AgendaEditorProps) {
  const { data: agenda, isLoading, isError } = useAgenda(agendaId);
  const addSection = useAddSection();
  const reorderSections = useReorderSections();
  const updateAgenda = useUpdateAgenda();
  const { data: teams = [] } = useTeams();

  const [orderedSections, setOrderedSections] = useState<AgendaSection[]>([]);
  const [durationOverrides, setDurationOverrides] = useState<Record<string, number>>({});
  const [titleInput, setTitleInput] = useState('');

  useEffect(() => {
    if (agenda?.sections) {
      const sorted = [...agenda.sections].sort((a, b) => a.sort_order - b.sort_order);
      setOrderedSections(sorted);
      setDurationOverrides({});
    }
  }, [agenda?.sections]);

  useEffect(() => {
    if (agenda?.title) setTitleInput(agenda.title);
  }, [agenda?.title]);

  const handleTitleBlur = () => {
    const trimmed = titleInput.trim();
    if (trimmed && trimmed !== agenda?.title) {
      updateAgenda.mutate({ id: agendaId, input: { title: trimmed } });
    } else {
      setTitleInput(agenda?.title ?? '');
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = orderedSections.findIndex((s) => s.id === active.id);
    const newIndex = orderedSections.findIndex((s) => s.id === over.id);
    const reordered = arrayMove(orderedSections, oldIndex, newIndex);
    setOrderedSections(reordered);

    reorderSections.mutate(
      { agendaId, sectionIds: reordered.map((s) => s.id) },
      { onError: () => toast.error('Failed to save order') },
    );
  };

  const handleAddCustomSection = () => {
    addSection.mutate({
      agendaId,
      input: { name: 'New Section', tool_type: null, duration_seconds: 300 },
    });
  };

  const handleAddToolSection = (tool: { name: string; toolType: ToolType; duration: number }) => {
    addSection.mutate({
      agendaId,
      input: { name: tool.name, tool_type: tool.toolType, duration_seconds: tool.duration },
    });
  };

  const handleTeamChange = (value: string) => {
    const teamId = value === 'none' ? undefined : value;
    updateAgenda.mutate({ id: agendaId, input: { team_id: teamId } });
  };

  const handleDurationChange = (sectionId: string, seconds: number) => {
    setDurationOverrides((prev) => ({ ...prev, [sectionId]: seconds }));
  };

  const handleSectionDeleted = (sectionId: string) => {
    setOrderedSections((prev) => prev.filter((s) => s.id !== sectionId));
    setDurationOverrides((prev) => {
      const next = { ...prev };
      delete next[sectionId];
      return next;
    });
  };

  const totalSeconds = orderedSections.reduce((sum, s) => {
    const override = durationOverrides[s.id];
    return sum + (override !== undefined ? override : s.duration_seconds);
  }, 0);

  if (isLoading) {
    return (
      <div className="space-y-4 p-1">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (isError || !agenda) {
    return (
      <div className="py-12 text-center text-muted-foreground">
        <p>Failed to load agenda.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Title + Team row */}
      <div className="flex items-center gap-3 flex-wrap">
        <Input
          value={titleInput}
          onChange={(e) => setTitleInput(e.target.value)}
          onBlur={handleTitleBlur}
          className="flex-1 min-w-0 h-9 text-lg font-semibold border-transparent hover:border-input focus:border-input bg-transparent px-2"
          aria-label="Agenda title"
        />
        <Select value={agenda.team_id ?? 'none'} onValueChange={handleTeamChange}>
          <SelectTrigger className="w-[160px] h-8 text-sm">
            <SelectValue placeholder="All teams" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">All teams</SelectItem>
            {teams.map((t) => (
              <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="outline" size="sm" className="px-2 h-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              className="text-destructive"
              onClick={() => toast.info('Delete agenda coming soon')}
            >
              Delete Agenda
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Section List */}
      <div className="border rounded-lg overflow-hidden bg-background shadow-sm">
        {/* Column Headers */}
        <div className="flex items-center gap-3 px-4 py-2 bg-muted/50 border-b text-xs font-medium text-muted-foreground uppercase tracking-wide">
          <div className="w-5 shrink-0" />
          <div className="flex-1">Section</div>
          <div className="w-24 text-center shrink-0">Duration</div>
          <div className="w-8 shrink-0" />
        </div>

        {orderedSections.length === 0 ? (
          <div className="py-12 text-center text-muted-foreground">
            <p className="text-sm">No sections yet. Add one below.</p>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={orderedSections.map((s) => s.id)}
              strategy={verticalListSortingStrategy}
            >
              {orderedSections.map((section) => (
                <AgendaSectionRow
                  key={section.id}
                  section={section}
                  agendaId={agenda.id}
                  onDurationChange={handleDurationChange}
                  onDeleted={handleSectionDeleted}
                />
              ))}
            </SortableContext>
          </DndContext>
        )}

        {/* Total Duration Footer */}
        <div className="flex items-center justify-end gap-2 px-4 py-3 bg-muted/30 border-t text-sm text-muted-foreground">
          <span className="font-medium text-foreground">Total Duration:</span>
          <span>{formatTotalDuration(totalSeconds)}</span>
        </div>
      </div>

      {/* Bottom Action Bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAddCustomSection}
          disabled={addSection.isPending}
          className="gap-2"
        >
          <Plus className="h-4 w-4" />
          Add Custom Section
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={addSection.isPending}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              Add Tool Section
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {TOOL_SECTIONS.map((tool) => (
              <DropdownMenuItem key={tool.toolType} onClick={() => handleAddToolSection(tool)}>
                {tool.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
